<script setup lang="ts">
import { actionTransformer, ActionFields } from '~/components/cms/Action/actionHelpers'
import { AnchorProps } from '~/components/ui/Anchor.vue'
import { Document } from '@contentful/rich-text-types'
import { BREAKPOINTS } from '~/config/common'

export type Panel = {
  id?: string
  title: string
  type?: 'utilityPanel' | 'richTextPanel' | 'sectionPanel'
  actions?: ActionFields[]
  content?: Document
  panels?: Panel[]
  panelLink?: AnchorProps
  supportingData?: any
  personalizationTags?: PersonalizationTags
}

type Props = {
  open: boolean
  id: string
  title: string
  url: string
  panelLink?: AnchorProps
  panels?: Panel[]
  supportingData?: any
}

const props = defineProps<Props>()
const emits = defineEmits(['menuPanelClose'])

const activeSubPanel = ref('')

const visiblePanels = computed(() => props.panels?.filter((panel) => personalizationControl(panel.personalizationTags)))

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      activeSubPanel.value = ''
    }
  },
)

function handleSubPanelOpen(panel: Panel) {
  const desktop = window.innerWidth >= BREAKPOINTS.desktopXL
  if (!desktop && panel.id) {
    activeSubPanel.value = panel.id
  }
}

function handleSubPanelClose(e: Event, skipEvent?: boolean) {
  if (activeSubPanel.value) {
    e.stopPropagation()
  }
  activeSubPanel.value = ''
}

const trackMenuBackEvent = () => {
  pushEventToDataLayer('custom.menu.back', {
    group: 'mobile menu',
    label: props.title,
    type: 'navigationItem',
    info: {
      location: 'sidebar',
      url: props.url,
      element: document.getElementById(props.id)?.tagName,
    },
  })
}

const gaOptions: DataLayerOptional = {
  eventName: 'custom.menu.link',
  type: 'UtilityPanelNavigation',
  runtimeProps: [
    { name: 'group', value: () => (window.innerWidth >= BREAKPOINTS.desktopXL ? 'mega menu' : 'mobile menu') },
  ],
  info: {
    location: 'header',
  },
}

const handleHeaderBack = (e: MouseEvent) => {
  emits('menuPanelClose', e, true)
  trackMenuBackEvent()
}
</script>

<template>
  <div
    class="menu-panels"
    data-testid="menu-panels"
    :id="id"
    :class="open ? 'menu-panels--open' : 'menu-panels--close'"
  >
    <div class="menu-panels__header">
      <div class="menu-panels__header-row">
        <button class="menu-panels__header-back" @click="handleHeaderBack">
          <svg-icon name="mhj:arrow-carousel-left" /> {{ $t('header.menu_drawer.buttons.back') }}
        </button>
        <button
          class="menu-panels__header-close"
          @click="$emit('menuPanelClose', $event)"
          :title="$t('screen_reader.buttons.close')"
        >
          <svg-icon name="mhj:cross" />
        </button>
      </div>
      <h3 class="menu-panels__header-title">{{ title }}</h3>
    </div>

    <ul class="menu-panels__scroll">
      <template v-if="visiblePanels">
        <template v-for="(panel, i) in visiblePanels">
          <li v-if="panel.type === 'sectionPanel'" class="menu-panels__panel menu-panels__panel--section">
            <template v-if="i === 0">
              <Anchor
                v-if="panelLink"
                v-bind="panelLink"
                class="menu-panels__panel-link"
                :add-to-site-navigation-schema="true"
              />
            </template>
            <button
              class="menu-panels__sub-link"
              aria-haspopup="true"
              :aria-expanded="activeSubPanel === panel.id"
              :aria-controls="panel.id"
              @click="handleSubPanelOpen(panel)"
            >
              {{ panel.panelLink?.text || panel.title }}
              <svg-icon name="mhj:arrow-carousel-right" />
            </button>
            <MenuSubPanels
              :id="panel.id || ''"
              :title="panel.panelLink?.text || panel.title || ''"
              :parentTitle="title"
              :panelLink="panel.panelLink"
              :panels="panel.panels"
              :open="panel.id === activeSubPanel"
              @menu-sub-panel-close="handleSubPanelClose"
              @menu-panel-close="$emit('menuPanelClose', $event)"
            />
          </li>
          <li v-else-if="panel.actions" class="menu-panels__panel">
            <template v-if="i === 0">
              <Anchor
                v-if="panelLink"
                v-bind="panelLink"
                :add-to-site-navigation-schema="true"
                class="menu-panels__panel-link"
              />
            </template>
            <h5 v-if="panel.title" class="menu-panels__heading">{{ panel.title }}</h5>
            <ul>
              <template v-for="action in panel.actions">
                <li v-if="action?.fields?.text">
                  <Anchor
                    data-testid="menu__link"
                    v-bind="actionTransformer(action)"
                    :ga-options="gaOptions"
                    :log-click="true"
                    :add-to-site-navigation-schema="true"
                  />
                </li>
              </template>
            </ul>
          </li>
          <li v-else-if="panel.type === 'richTextPanel'" class="menu-panels__panel">
            <template v-if="i === 0">
              <Anchor
                v-if="panelLink"
                v-bind="panelLink"
                :add-to-site-navigation-schema="true"
                class="menu-panels__panel-link"
              />
            </template>
            <RichTextPanel :content="panel?.content" :skipContainer="true" />
          </li>
        </template>
      </template>
    </ul>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.menu-panels {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: var(--surface-light-00);
  display: none;
  animation: slide-out-to-right 0.3s cubic-bezier(0.25, 0, 0.3, 1); /* closing effect */

  @media (--desktop) {
    animation: none;
  }

  &.menu-panels--open {
    display: block;
    z-index: var(--z-header);
    animation: slide-in-from-right 0.5s cubic-bezier(0.25, 0, 0.3, 1); /* opening effect */
    overflow: auto;
    overscroll-behavior: none;
    scrollbar-width: none;

    @media (--desktop) {
      animation: none;
    }
  }

  .menu-panels__header {
    background: var(--surface-light-00);
    border-bottom: 1px solid var(--border-neutral-20);
    position: sticky;
    top: 0;
    padding-block: var(--space-50) var(--space-60);
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    align-items: center;

    .menu-panels__header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-inline: var(--space-50);
      width: 100%;
    }

    .menu-panels__header-back {
      display: flex;
      align-items: center;
      gap: var(--space-30);
      font-size: var(--text-small);
      cursor: pointer;

      svg {
        height: var(--space-50);
      }
    }

    .menu-panels__header-title {
      @extend %heading-2;
      text-transform: uppercase;
      margin-bottom: 0;
    }

    @media (--desktop) {
      display: none;
    }
  }

  .menu-panels__scroll {
    overflow-x: hidden;
    scrollbar-width: none;
    padding-inline: var(--space-60);
  }

  .menu-panels__heading {
    font-weight: bold;
    margin-bottom: var(--space-60);
    text-transform: uppercase;
    min-width: max-content;
    max-width: 13.5rem;
    font-size: var(--text-large);
    @media (--desktop) {
      font-size: var(--text-base);
    }
  }

  .menu-panels__panel {
    width: 100%;
    padding-top: var(--space-60);
    border-bottom: 1px solid var(--border-neutral-20);

    &:last-child {
      border-bottom: none;
      @media (--mobile-tablet-only) {
        margin-bottom: var(--space-80);
      }
    }

    li {
      margin-bottom: var(--space-60);
    }

    & .rich-text {
      a {
        text-decoration: underline;
      }
    }
    a {
      display: flex;
      text-decoration: none;
      font-size: var(--text-large);
      @media (--desktop) {
        max-width: 13.5rem;
        font-size: var(--text-small);
      }

      &:hover {
        text-decoration: underline;
      }
    }

    a.link-text {
      font-weight: bold;
      font-size: var(--text-large);
      line-height: var(--text-large);
      @media (--desktop) {
        font-size: var(--text-small);
        line-height: var(--text-small);
      }
    }

    .menu-panels__panel-link {
      text-decoration: none;
      font-size: var(--text-base);
      font-weight: bold;
      width: 100%;
      padding-bottom: var(--space-60);
      border-bottom: 1px solid var(--border-neutral-20);
      margin-bottom: var(--space-60);
    }

    .rich-text__block-entry {
      margin: 0;
    }
  }

  .menu-panels__sub-link {
    font-size: var(--text-large);
    font-weight: bold;
    color: inherit;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding-block: var(--space-50);
    cursor: pointer;

    svg {
      height: 0.7rem;
      margin-right: 0.3rem;
    }

    @media (--desktop) {
      display: none;
    }
  }

  .menu-panels__panel--section {
    padding-top: 0;
  }
  .rich-text {
    p {
      font-size: var(--text-large);
      @media (--desktop) {
        font-size: var(--text-small);
      }
    }
    img {
      aspect-ratio: 16/9;
      object-fit: cover;
    }
  }
}

@media (--desktop) {
  .menu-panels {
    top: var(--header-height);
    padding-bottom: var(--space-70);
    height: auto;
    overflow: hidden;
    border-top: 0.4rem solid var(--surface-accent);

    .menu-panels__scroll {
      padding-top: 0;
      height: 100%;
      overflow: visible;
      display: grid;
      grid-auto-flow: column;
      grid-template-rows: auto auto;
      justify-content: space-between;
      margin: 0 auto;
      min-width: 57rem;
      max-width: fit-content;
      column-gap: var(--space-70);
    }

    .menu-panels__panel {
      min-width: min-content;
      border-bottom: none;
      grid-row: 1 / -1;
      padding-right: var(--space-60);

      .rich-text-panel {
        width: 10.5rem;
        .rich-text {
          p {
            margin-bottom: var(--space-30);
          }
          a {
            text-decoration: underline;
          }
        }
      }
    }

    .menu-panels__panel:first-child {
      position: relative;
      z-index: 0;
      padding-inline: var(--space-60);
      min-width: 13.5rem;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: -1.5rem; /* account for padding */
        left: -100vw;
        right: 0;
        background: var(--surface-light-10);
        z-index: -1;
      }

      a {
        font-size: var(--text-base);
      }
    }

    .menu-panels__panel--section {
      display: contents;
    }
  }
}

@media (--mobile-tablet-only) {
  .menu-panels,
  .menu-panels__header {
    max-width: 28rem;
  }
}
</style>
